/**
 * Reconstitution and dose math.
 *
 * This module is a CALCULATOR and a LOG. It contains no dosing
 * recommendations, no safety thresholds, and no clinical opinions. Every
 * quantity it produces is derived arithmetically from numbers the user
 * entered.
 *
 * ---------------------------------------------------------------------------
 * The model
 * ---------------------------------------------------------------------------
 * A vial holds a known mass of lyophilised (freeze-dried) peptide. Adding a
 * known volume of diluent (bacteriostatic water) yields a solution whose
 * concentration is:
 *
 *     concentration (mg/mL) = peptide mass (mg) / diluent volume (mL)
 *
 * To deliver a dose of a given mass, draw the volume that contains it:
 *
 *     volume (mL) = dose (mg) / concentration (mg/mL)
 *
 * Insulin syringes are not graduated in millilitres but in "units" on a scale
 * fixed by the syringe type. A U-100 syringe divides 1 mL into 100 units, so:
 *
 *     units = volume (mL) x units per mL
 *
 * Worked example (the default protocol in this app):
 *     10 mg vial + 2 mL bacteriostatic water  -> 5 mg/mL
 *     1.5 mg dose / 5 mg/mL                   -> 0.3 mL
 *     0.3 mL x 100 units/mL                   -> 30 units on a U-100 syringe
 *
 * ---------------------------------------------------------------------------
 * Assumption: powder volume displacement is ignored
 * ---------------------------------------------------------------------------
 * The lyophilised powder occupies a small volume of its own, so the true
 * solution volume is fractionally larger than the diluent volume added, which
 * makes the true concentration fractionally lower than the figure above. For
 * peptide masses at this scale the displaced volume is on the order of
 * microlitres (roughly 0.01 mL for 10 mg) -- about 0.5% at a 2 mL fill, well
 * below the precision of a syringe graduated in 0.01 mL steps.
 *
 * This module therefore defines concentration against the DILUENT volume,
 * which is both the convention used on every reconstitution chart and the
 * number the user actually measured. It does NOT apply a displacement
 * correction, because doing so silently would make the app disagree with every
 * chart the user cross-checks it against. The assumption is stated here rather
 * than hidden in a constant.
 */
import { isPositive, round, roundToStep } from './num';

// ---------------------------------------------------------------------------
// Syringes
// ---------------------------------------------------------------------------

/**
 * The scale printed on the barrel. This is a property of the syringe type, not
 * of its capacity: a "0.3 mL U-100 syringe" is a U-100 scale with a 30 unit
 * capacity. U-40 is the veterinary scale and is included because U-40 syringes
 * are easy to buy by accident; drawing a U-100 number of units on a U-40
 * barrel delivers 2.5x the intended volume.
 */
export type SyringeScale = 'U100' | 'U40' | 'mL';

export interface Syringe {
  /** Stable key used in stored records. */
  id: string;
  /** Human label. */
  label: string;
  scale: SyringeScale;
  /** Units marked per millilitre. For the 'mL' scale this is 1 (volume is read directly). */
  unitsPerMl: number;
  /** Barrel capacity, expressed in this syringe's own units. */
  capacityUnits: number;
  /**
   * Smallest interval actually marked on the barrel -- the finest amount that
   * can be drawn deliberately rather than estimated between marks.
   *
   * Graduation spacing varies by manufacturer. These are the common defaults:
   * 0.3 mL and 0.5 mL U-100 barrels are typically marked every 1 unit, while
   * 1 mL U-100 barrels are typically marked every 2 units. A user whose
   * syringes differ can override this per syringe in settings.
   */
  increment: number;
}

export const SYRINGES: Record<string, Syringe> = {
  'u100-0.3': { id: 'u100-0.3', label: '0.3 mL U-100 (30 u)', scale: 'U100', unitsPerMl: 100, capacityUnits: 30, increment: 1 },
  'u100-0.5': { id: 'u100-0.5', label: '0.5 mL U-100 (50 u)', scale: 'U100', unitsPerMl: 100, capacityUnits: 50, increment: 1 },
  'u100-1.0': { id: 'u100-1.0', label: '1 mL U-100 (100 u)', scale: 'U100', unitsPerMl: 100, capacityUnits: 100, increment: 2 },
  'u40-1.0': { id: 'u40-1.0', label: '1 mL U-40 (40 u)', scale: 'U40', unitsPerMl: 40, capacityUnits: 40, increment: 1 },
  'ml-1.0': { id: 'ml-1.0', label: '1 mL syringe (mL scale)', scale: 'mL', unitsPerMl: 1, capacityUnits: 1, increment: 0.01 },
};

export const DEFAULT_SYRINGE_ID = 'u100-0.3';

/**
 * A dialable pen (e.g. a PepPen cartridge pen) behaves like a U-100 syringe for
 * the purposes of this math: the dial is graduated in insulin units, so the
 * same volume-per-unit relationship holds. It is modelled as a syringe whose
 * increment is the dial's smallest click.
 */
export function penAsSyringe(capacityUnits: number, increment = 1): Syringe {
  return { id: 'pen', label: 'Pen dial (units)', scale: 'U100', unitsPerMl: 100, capacityUnits, increment };
}

// ---------------------------------------------------------------------------
// Core arithmetic
// ---------------------------------------------------------------------------

/**
 * Concentration of a reconstituted vial, in mg/mL.
 * @throws RangeError when either input is not a positive finite number.
 */
export function concentrationMgPerMl(vialMg: number, diluentMl: number): number {
  if (!isPositive(vialMg)) throw new RangeError('vial mass must be greater than zero');
  if (!isPositive(diluentMl)) throw new RangeError('diluent volume must be greater than zero');
  return round(vialMg / diluentMl, 9);
}

/**
 * Diluent volume needed to hit a target concentration.
 * Answers "how much water do I add to make this vial 5 mg/mL?".
 * @throws RangeError on non-positive input.
 */
export function diluentForConcentration(vialMg: number, targetMgPerMl: number): number {
  if (!isPositive(vialMg)) throw new RangeError('vial mass must be greater than zero');
  if (!isPositive(targetMgPerMl)) throw new RangeError('target concentration must be greater than zero');
  return round(vialMg / targetMgPerMl, 9);
}

/**
 * Volume (mL) containing a given mass at a given concentration.
 * @throws RangeError on non-positive concentration or negative dose.
 */
export function doseVolumeMl(doseMg: number, mgPerMl: number): number {
  if (!isPositive(mgPerMl)) throw new RangeError('concentration must be greater than zero');
  if (!Number.isFinite(doseMg) || doseMg < 0) throw new RangeError('dose must be zero or greater');
  return round(doseMg / mgPerMl, 9);
}

/** Volume (mL) -> syringe units. */
export function volumeToUnits(ml: number, unitsPerMl: number): number {
  if (!isPositive(unitsPerMl)) throw new RangeError('units per mL must be greater than zero');
  if (!Number.isFinite(ml) || ml < 0) throw new RangeError('volume must be zero or greater');
  return round(ml * unitsPerMl, 9);
}

/** Syringe units -> volume (mL). */
export function unitsToVolume(units: number, unitsPerMl: number): number {
  if (!isPositive(unitsPerMl)) throw new RangeError('units per mL must be greater than zero');
  if (!Number.isFinite(units) || units < 0) throw new RangeError('units must be zero or greater');
  return round(units / unitsPerMl, 9);
}

/** Dose mass (mg) -> syringe units, at a given concentration. */
export function doseToUnits(doseMg: number, mgPerMl: number, unitsPerMl: number): number {
  return volumeToUnits(doseVolumeMl(doseMg, mgPerMl), unitsPerMl);
}

/** Syringe units -> dose mass (mg), at a given concentration. The exact inverse of `doseToUnits`. */
export function unitsToDose(units: number, mgPerMl: number, unitsPerMl: number): number {
  if (!isPositive(mgPerMl)) throw new RangeError('concentration must be greater than zero');
  return round(unitsToVolume(units, unitsPerMl) * mgPerMl, 9);
}

// ---------------------------------------------------------------------------
// Dose planning -- the honest-rounding layer
// ---------------------------------------------------------------------------

export interface DosePlanInput {
  /** Peptide mass in the vial, mg. */
  vialMg: number;
  /** Diluent added, mL. */
  diluentMl: number;
  /** Target dose, mg. */
  doseMg: number;
  /** The syringe or pen being drawn with. */
  syringe: Syringe;
}

export interface DosePlan {
  ok: true;
  /** mg/mL of the reconstituted solution. */
  concentrationMgPerMl: number;
  /** Volume containing exactly the target dose, mL. */
  exactVolumeMl: number;
  /** Units containing exactly the target dose -- may fall between barrel marks. */
  exactUnits: number;
  /** Nearest amount actually markable on this barrel. */
  drawnUnits: number;
  /** Volume of `drawnUnits`, mL. */
  drawnVolumeMl: number;
  /** Mass actually delivered if `drawnUnits` is drawn, mg. */
  deliveredMg: number;
  /** deliveredMg - doseMg. Signed: negative means under the target. */
  deltaMg: number;
  /** `deltaMg` as a percentage of the target dose. Zero when the target is zero. */
  deltaPct: number;
  /** True when the exact amount lands on a barrel mark, so nothing is lost to rounding. */
  exactOnMark: boolean;
  /** True when the dose needs more volume than the barrel holds. */
  exceedsCapacity: boolean;
}

export interface DosePlanError {
  ok: false;
  issues: string[];
}

/**
 * Work out what to draw, and what that actually delivers.
 *
 * The distinction this function exists to make: `exactUnits` is the
 * mathematically correct amount, `drawnUnits` is what the barrel can express,
 * and `deliveredMg` is what the body receives as a result. A calculator that
 * reports only the first of those is quietly misleading whenever the dose does
 * not land on a mark, and a calculator that reports only the second hides the
 * discrepancy. This reports all three and the delta between them, and leaves
 * the interpretation to the user.
 *
 * Returns a discriminated union rather than throwing, because the inputs come
 * straight from text fields.
 */
export function planDose(input: DosePlanInput): DosePlan | DosePlanError {
  const { vialMg, diluentMl, doseMg, syringe } = input;
  const issues: string[] = [];

  if (!isPositive(vialMg)) issues.push('Vial amount must be greater than zero.');
  if (!isPositive(diluentMl)) issues.push('Diluent volume must be greater than zero.');
  if (!Number.isFinite(doseMg) || doseMg <= 0) issues.push('Dose must be greater than zero.');
  if (!syringe || !isPositive(syringe.unitsPerMl)) issues.push('Pick a syringe.');
  if (syringe && !isPositive(syringe.increment)) issues.push('Syringe increment must be greater than zero.');
  if (issues.length) return { ok: false, issues };

  const concentration = concentrationMgPerMl(vialMg, diluentMl);
  const exactVolumeMl = doseVolumeMl(doseMg, concentration);
  const exactUnits = volumeToUnits(exactVolumeMl, syringe.unitsPerMl);
  const drawnUnits = roundToStep(exactUnits, syringe.increment);
  const drawnVolumeMl = unitsToVolume(drawnUnits, syringe.unitsPerMl);
  const deliveredMg = round(drawnVolumeMl * concentration, 9);
  const deltaMg = round(deliveredMg - doseMg, 9);

  return {
    ok: true,
    concentrationMgPerMl: concentration,
    exactVolumeMl,
    exactUnits,
    drawnUnits,
    drawnVolumeMl,
    deliveredMg,
    deltaMg,
    deltaPct: doseMg > 0 ? round((deltaMg / doseMg) * 100, 6) : 0,
    exactOnMark: Math.abs(exactUnits - drawnUnits) < 1e-9,
    exceedsCapacity: exactUnits > syringe.capacityUnits + 1e-9,
  };
}

// ---------------------------------------------------------------------------
// Vial inventory
// ---------------------------------------------------------------------------

/**
 * Whole doses a freshly reconstituted vial yields.
 *
 * `wasteMl` covers volume that leaves the vial without being injected -- pen
 * priming, dead space, a fumbled draw. It is subtracted as a mass equivalent
 * before the division. Partial doses are not counted: a vial with 1.4 doses
 * left yields 1 full dose.
 */
export function dosesPerVial(vialMg: number, doseMg: number, wasteMl = 0, mgPerMl?: number): number {
  if (!isPositive(vialMg)) throw new RangeError('vial mass must be greater than zero');
  if (!isPositive(doseMg)) throw new RangeError('dose must be greater than zero');
  if (!Number.isFinite(wasteMl) || wasteMl < 0) throw new RangeError('waste volume must be zero or greater');
  const wasteMg = wasteMl > 0 ? round(wasteMl * (mgPerMl ?? 0), 9) : 0;
  const usable = round(vialMg - wasteMg, 9);
  if (usable <= 0) return 0;
  return Math.floor(round(usable / doseMg, 9));
}

/**
 * Mass left in a vial after a set of withdrawals.
 * `withdrawnMg` is the sum of every dose logged against the vial plus any
 * recorded priming or spillage, already expressed as mass.
 * Clamped at zero -- a negative remainder means the log is wrong, not that the
 * vial owes mass back.
 */
export function remainingMg(vialMg: number, withdrawnMg: number): number {
  if (!isPositive(vialMg)) throw new RangeError('vial mass must be greater than zero');
  if (!Number.isFinite(withdrawnMg) || withdrawnMg < 0) throw new RangeError('withdrawn mass must be zero or greater');
  return Math.max(0, round(vialMg - withdrawnMg, 9));
}

/** Volume left in a vial, given the diluent it was reconstituted with. */
export function remainingMl(diluentMl: number, withdrawnMl: number): number {
  if (!isPositive(diluentMl)) throw new RangeError('diluent volume must be greater than zero');
  if (!Number.isFinite(withdrawnMl) || withdrawnMl < 0) throw new RangeError('withdrawn volume must be zero or greater');
  return Math.max(0, round(diluentMl - withdrawnMl, 9));
}

/** Whole doses remaining, given mass already withdrawn. */
export function dosesRemaining(vialMg: number, withdrawnMg: number, doseMg: number): number {
  if (!isPositive(doseMg)) throw new RangeError('dose must be greater than zero');
  const left = remainingMg(vialMg, withdrawnMg);
  return Math.floor(round(left / doseMg, 9));
}
